import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate, UserRoles } from '../lib/utils';

// Fetch every page of a paginated endpoint (backend caps page_size at 200)
const fetchAllPages = async (baseUrl) => {
  const sep = baseUrl.includes('?') ? '&' : '?';
  let page = 1;
  let totalPages = 1;
  let items = [];
  do {
    const res = await axios.get(`${baseUrl}${sep}page=${page}&page_size=200`);
    const data = res.data.items || res.data;
    items = items.concat(data);
    totalPages = res.data.total_pages || 1;
    page += 1;
  } while (page <= totalPages);
  return items;
};
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  Building2, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Users,
  Calendar,
  GraduationCap,
  Award,
  CheckCircle,
  X,
  AlertTriangle,
  AlertCircle,
  User,
  Send,
  RefreshCw
} from 'lucide-react';

const CompanyDetail = ({ companyId: propCompanyId }) => {
  const { id: paramId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Use prop companyId if provided (from MyCompany redirect), otherwise use URL param
  const id = propCompanyId || paramId;
  const isCompanyUser = user?.role === UserRoles.COMPANY;
  
  const [company, setCompany] = useState(null);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [extraParticipants, setExtraParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classPage, setClassPage] = useState(1);
  const CLASSES_PER_PAGE = 10;
  const [activeYear, setActiveYear] = useState(null);

  // Per-class reminder dialog
  const [reminderClass, setReminderClass] = useState(null); // class doc when dialog open
  const [reminderStudents, setReminderStudents] = useState([]); // [{student_id, student_name, certificates: [...]}]
  const [reminderSelected, setReminderSelected] = useState([]); // student_ids
  const [reminderSending, setReminderSending] = useState(false);

  usePageTitle(company?.name || 'Company');

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) {
          setError('No company ID provided.');
          setLoading(false);
          return;
        }

        // Fetch company details
        const companyRes = await axios.get(`${API_URL}/companies/${id}`);
        setCompany(companyRes.data);

        // For company users, use the employees endpoint; for admins, query persons by company
        let companyStudents;
        if (isCompanyUser) {
          const employeesRes = await axios.get(`${API_URL}/companies/${id}/employees`);
          companyStudents = employeesRes.data;
        } else {
          companyStudents = await fetchAllPages(`${API_URL}/persons?company_id=${id}`);
        }
        setStudents(companyStudents);
        
        // Fetch courses for expiration info
        const coursesRes = await axios.get(`${API_URL}/courses`);
        setCourses(coursesRes.data);

        // Fetch this company's classes (server-side filter; paginated —
        // fetching all classes globally would be capped at 200 and miss older ones)
        const allClassesData = await fetchAllPages(`${API_URL}/classes?company_id=${id}`);
        setAllClasses(allClassesData);

        // Find classes where company students participated
        const studentIds = companyStudents.map(s => s.id);
        // Server returns exactly this company's classes: directly-linked
        // (in-house) classes AND classes containing registered employees.
        // Do NOT drop directly-linked classes whose participants aren't
        // registered as employees — they still belong to this company.
        const companyClasses = [...allClassesData];

        // Sort by date descending
        companyClasses.sort((a, b) => new Date(b.date_start) - new Date(a.date_start));
        setClasses(companyClasses);

        // Resolve participants of directly-linked classes who are not
        // registered employees (so their names/certs still display)
        const employeeIdSet = new Set(studentIds);
        const extraIds = [...new Set(
          companyClasses
            .filter(c => c.company_id === id)
            .flatMap(c => c.student_ids || [])
            .filter(pid => !employeeIdSet.has(pid))
        )];
        let extras = [];
        for (let i = 0; i < extraIds.length; i += 50) {
          const chunk = extraIds.slice(i, i + 50);
          const res = await axios.get(`${API_URL}/persons/lookup?ids=${chunk.join(',')}`).catch(() => ({ data: { items: [] } }));
          extras = extras.concat(res.data.items || []);
        }
        setExtraParticipants(extras);

        // Fetch ALL certificates of the company's employees in batches of 50
        // ids per request (covers per-class tables and employee status summaries)
        const allPersonIds = [...studentIds, ...extraIds];
        const certPromises = [];
        for (let i = 0; i < allPersonIds.length; i += 50) {
          const chunk = allPersonIds.slice(i, i + 50);
          certPromises.push(
            fetchAllPages(`${API_URL}/certificates?person_ids=${chunk.join(',')}`).catch(() => [])
          );
        }
        const certArrays = await Promise.all(certPromises);
        setCertificates(certArrays.flat());

      } catch (error) {
        console.error('Failed to fetch company data:', error);
        setError('Failed to load company information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isCompanyUser]);

  // Get students from this company who participated in a specific class
  const getClassStudents = (cls) => {
    // For classes directly linked to this company, show ALL participants
    // (including those not registered as employees); otherwise show the
    // registered employees who attended.
    if (cls.company_id === id) {
      const seen = new Set();
      return [...students, ...extraParticipants].filter(s => {
        if (!cls.student_ids?.includes(s.id) || seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });
    }
    return students.filter(s => cls.student_ids?.includes(s.id));
  };
  
  // Get certificate number for a student in a specific class
  const getCertificateNumber = (studentId, classId) => {
    const certificate = certificates.find(
      cert => cert.person_id === studentId && cert.class_id === classId
    );
    return certificate?.certificate_number || '-';
  };
  
  // Get certificate status for a student in a specific class
  const getCertificateStatus = (studentId, classId) => {
    const certificate = certificates.find(
      cert => cert.person_id === studentId && cert.class_id === classId
    );
    if (!certificate) return null;
    
    // Use the certificate's expiry_date if available
    if (certificate.expiry_date) {
      const expiryDate = new Date(certificate.expiry_date);
      const now = new Date();
      
      if (expiryDate < now) {
        return 'expired';
      } else if (expiryDate < new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)) {
        return 'expiring';
      }
      return 'certified';
    }
    
    // Fallback: Calculate from class date and course expiration years
    const cls = classes.find(c => c.id === classId) || allClasses.find(c => c.id === classId);
    const course = courses.find(c => c.id === certificate.course_id);
    
    if (!course) return 'certified';
    
    const baseDate = certificate.issue_date 
      ? new Date(certificate.issue_date)
      : cls?.date_completed 
        ? new Date(cls.date_completed)
        : cls?.date_start 
          ? new Date(cls.date_start)
          : null;
    
    if (!baseDate) return 'certified';
    
    const expiryDate = new Date(baseDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + (course.expiration_years || 3));
    const now = new Date();
    
    if (expiryDate < now) {
      return 'expired';
    } else if (expiryDate < new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)) {
      return 'expiring';
    }
    return 'certified';
  };

  // Get the most urgent status for a student across all their certificates
  const getStudentCertificateStatus = (studentId) => {
    const studentCerts = certificates.filter(cert => cert.person_id === studentId);
    if (studentCerts.length === 0) return null;
    
    let hasExpired = false;
    let hasExpiring = false;
    
    for (const cert of studentCerts) {
      if (cert.expiry_date) {
        const expiryDate = new Date(cert.expiry_date);
        const now = new Date();
        
        if (expiryDate < now) {
          hasExpired = true;
        } else if (expiryDate < new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)) {
          hasExpiring = true;
        }
      }
    }
    
    if (hasExpired) return 'expired';
    if (hasExpiring) return 'expiring';
    return 'certified';
  };
  
  // Calculate expiry date for a class based on course expiration years
  const getExpiryDate = (cls) => {
    const course = courses.find(c => c.id === cls.course_id);
    const completionDate = cls.date_completed || cls.date_start;
    if (!course || !completionDate) return null;
    
    const startDate = new Date(completionDate);
    const expiryDate = new Date(startDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + (course.expiration_years || 3));
    return expiryDate;
  };

  // Get class status (same logic as Classes.jsx)
  const getClassStatus = (cls) => {
    if (cls.certificates_issued) {
      const course = courses.find(c => c.id === cls.course_id);
      const completionDate = cls.date_completed || cls.date_start;
      if (course && completionDate) {
        const expiryDate = new Date(completionDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + (course.expiration_years || 3));
        const now = new Date();
        
        if (expiryDate < now) {
          return 'expired';
        } else if (expiryDate < new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)) {
          return 'expiring';
        }
      }
      return 'certified';
    } else if (cls.date_completed) {
      return 'completed';
    }
    return 'in_progress';
  };

  // Build the list of students of this class whose certificate (for THIS class)
  // expires in the next 180 days (and is not already expired). Used to decide
  // whether to show the per-class Send-Reminder button and to populate the
  // selection dialog.
  const EXPIRY_REMINDER_WINDOW_DAYS = 180;
  const getExpiringStudentsForClass = (cls) => {
    const now = new Date();
    const cutoff = new Date(now.getTime() + EXPIRY_REMINDER_WINDOW_DAYS * 24 * 60 * 60 * 1000);
    const classStudents = getClassStudents(cls);
    const rows = [];
    for (const s of classStudents) {
      const certs = certificates.filter(
        c => c.person_id === s.id && c.class_id === cls.id && c.expiry_date,
      );
      const expiring = certs.filter(c => {
        const ed = new Date(c.expiry_date);
        return ed >= now && ed <= cutoff;
      });
      if (expiring.length > 0) {
        rows.push({
          student_id: s.id,
          student_name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
          certificates: expiring.map(c => ({
            certificate_id: c.id,
            certificate_number: c.certificate_number,
            course_name: c.course_name,
            expiry_date: c.expiry_date,
            issue_date: c.issue_date,
          })),
        });
      }
    }
    return rows;
  };

  const openReminderDialog = (cls) => {
    const rows = getExpiringStudentsForClass(cls);
    setReminderClass(cls);
    setReminderStudents(rows);
    setReminderSelected(rows.map(r => r.student_id));
  };

  const toggleReminderStudent = (sid) => {
    setReminderSelected(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    );
  };

  const handleSendClassReminder = async () => {
    if (!reminderClass) return;
    if (reminderSelected.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    setReminderSending(true);
    try {
      await axios.post(`${API_URL}/expiry-reminders/send`, {
        company_id: id,
        class_id: reminderClass.id,
        student_ids: reminderSelected,
        expiry_window: EXPIRY_REMINDER_WINDOW_DAYS,
      });
      toast.success(`Reminder sent for ${reminderClass.course_name}`);
      setReminderClass(null);
      setReminderStudents([]);
      setReminderSelected([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminderSending(false);
    }
  };

  // Group classes by training year (date_completed or date_start). Years are
  // returned newest-first so the latest year appears as the first/active tab.
  const getClassYear = (cls) => {
    const d = cls.date_completed || cls.date_start;
    if (!d) return null;
    const year = new Date(d).getUTCFullYear();
    return Number.isFinite(year) ? year : null;
  };
  const classesByYear = (() => {
    const map = {};
    for (const cls of classes) {
      const y = getClassYear(cls);
      if (!y) continue;
      (map[y] ||= []).push(cls);
    }
    return map;
  })();
  const availableYears = Object.keys(classesByYear)
    .map((y) => parseInt(y, 10))
    .sort((a, b) => b - a);
  const undatedClasses = classes.filter((c) => !getClassYear(c));

  // Initialise / re-sync the active tab to the latest year whenever the
  // classes list changes (e.g. after data load).
  useEffect(() => {
    if (availableYears.length === 0 && !undatedClasses.length) {
      setActiveYear(null);
      return;
    }
    const valid = new Set(availableYears.map(String));
    if (undatedClasses.length) valid.add('undated');
    if (!activeYear || !valid.has(String(activeYear))) {
      setActiveYear(availableYears.length ? String(availableYears[0]) : 'undated');
    }
    // Reset pagination when year changes
    setClassPage(1);
  }, [classes.length]);

  // Classes to display for the currently-selected year tab
  const yearClasses = activeYear === 'undated'
    ? undatedClasses
    : (classesByYear[activeYear] || []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12" data-testid="company-detail-error">
        <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
        <h3 className="text-lg font-medium text-slate-900">Unable to load company</h3>
        <p className="text-slate-500 mt-2 max-w-md mx-auto">{error}</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900">Company not found</h3>
        {!isCompanyUser && (
          <Button variant="outline" className="mt-4" onClick={() => navigate('/companies')}>
            Back to Companies
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="company-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        {!isCompanyUser && (
          <Button variant="ghost" size="icon" onClick={() => navigate('/companies')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {company.name}
          </h1>
          <p className="text-slate-500 mt-1">
            {company.category && <Badge variant="outline" className="mr-2">{company.category}</Badge>}
            {company.city && company.country ? `${company.city}, ${company.country}` : 'Company Details'}
          </p>
        </div>
        {!isCompanyUser && (
          <Button 
            variant="outline"
            onClick={() => navigate(`/companies/${company.id}/edit`)}
            data-testid="edit-company-btn"
          >
            Edit Company
          </Button>
        )}
      </div>

      {/* Company Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#2C9DE1]" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {company.email && (
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <a href={`mailto:${company.email}`} className="font-medium text-[#2C9DE1] hover:underline">
                    {company.email}
                  </a>
                </div>
              </div>
            )}
            {company.telephone && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Telephone</p>
                  <p className="font-medium">{company.telephone}</p>
                </div>
              </div>
            )}
            {company.domain && (
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Domain</p>
                  <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer" className="font-medium text-[#2C9DE1] hover:underline">
                    {company.domain}
                  </a>
                </div>
              </div>
            )}
            {company.primary_contact && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Primary Contact</p>
                  <p className="font-medium">{company.primary_contact}</p>
                </div>
              </div>
            )}
            {company.address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Address</p>
                  <p className="font-medium">{company.address}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Employees</p>
                <p className="font-medium">{students.length} registered</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-[#2C9DE1]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{students.length}</p>
                <p className="text-sm text-slate-500">Employees</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{classes.length}</p>
                <p className="text-sm text-slate-500">Classes Attended</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {classes.filter(c => c.certificates_issued).length}
                </p>
                <p className="text-sm text-slate-500">Certified Classes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Classes with Company Students */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[#2C9DE1]" />
            Company Classes
          </CardTitle>
          <CardDescription>
            Classes booked by {company.name} or attended by its employees
          </CardDescription>
        </CardHeader>
        <CardContent>
          {classes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p>No classes found for this company</p>
            </div>
          ) : (
            <Tabs
              value={activeYear || ''}
              onValueChange={(v) => { setActiveYear(v); setClassPage(1); }}
              className="w-full"
            >
              <TabsList
                className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 mb-6"
                data-testid="company-classes-year-tabs"
              >
                {availableYears.map((y) => (
                  <TabsTrigger
                    key={y}
                    value={String(y)}
                    className="data-[state=active]:bg-white data-[state=active]:text-[#2C9DE1] data-[state=active]:shadow-sm"
                    data-testid={`company-classes-year-tab-${y}`}
                  >
                    {y}
                    <Badge variant="secondary" className="ml-2 bg-slate-200 text-slate-700 data-[state=active]:bg-sky-100">
                      {classesByYear[y].length}
                    </Badge>
                  </TabsTrigger>
                ))}
                {undatedClasses.length > 0 && (
                  <TabsTrigger
                    value="undated"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#2C9DE1] data-[state=active]:shadow-sm"
                    data-testid="company-classes-year-tab-undated"
                  >
                    Undated
                    <Badge variant="secondary" className="ml-2 bg-slate-200 text-slate-700">
                      {undatedClasses.length}
                    </Badge>
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value={activeYear || ''} className="mt-0">
                <div className="space-y-6">
                  {yearClasses
                    .slice((classPage - 1) * CLASSES_PER_PAGE, classPage * CLASSES_PER_PAGE)
                    .map((cls) => {
                const classStudents = getClassStudents(cls);
                const expiryDate = getExpiryDate(cls);
                const classStatus = getClassStatus(cls);
                const isExpired = expiryDate && expiryDate < new Date();
                const expiringRows = getExpiringStudentsForClass(cls);
                const canSendReminder = expiringRows.length > 0 && !isCompanyUser;
                return (
                  <div key={cls.id} className="border rounded-lg overflow-hidden">
                    {/* Class Header */}
                    <div 
                      className="bg-slate-50 p-4 flex items-center justify-between cursor-pointer hover:bg-slate-100"
                      onClick={() => navigate(`/classes/${cls.id}/edit`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          <GraduationCap className="w-5 h-5 text-[#2C9DE1]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{cls.course_name}</h3>
                          <p className="text-sm text-slate-500">
                            {formatDate(cls.date_start)} • {cls.duration_hours} hours
                            {cls.location_name && ` • ${cls.location_name}`}
                          </p>
                          {expiryDate && (
                            <p className="text-sm text-slate-500">
                              {isExpired ? 'Expired: ' : 'Expires: '}
                              <span className={isExpired ? 'text-red-500 font-medium' : 'text-slate-700'}>
                                {formatDate(expiryDate.toISOString())}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {canSendReminder && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-white border-amber-300 text-amber-800 hover:bg-amber-50"
                            data-testid={`class-send-reminder-btn-${cls.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openReminderDialog(cls);
                            }}
                          >
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            Send Reminder
                            <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-900">
                              {expiringRows.length}
                            </Badge>
                          </Button>
                        )}
                        <Badge variant="outline">
                          {classStudents.length} employee{classStudents.length !== 1 ? 's' : ''}
                        </Badge>
                        {(() => {
                          switch (classStatus) {
                            case 'expired':
                              return (
                                <Badge className="bg-red-100 text-red-800">
                                  <X className="w-3 h-3 mr-1" />
                                  Expired
                                </Badge>
                              );
                            case 'expiring':
                              return (
                                <Badge className="bg-amber-100 text-amber-800">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  &lt;90 to Expire
                                </Badge>
                              );
                            case 'certified':
                              return (
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Certified
                                </Badge>
                              );
                            case 'completed':
                              return (
                                <Badge className="bg-amber-100 text-amber-800">
                                  Completed
                                </Badge>
                              );
                            default:
                              return (
                                <Badge className="bg-blue-100 text-blue-800">
                                  In Progress
                                </Badge>
                              );
                          }
                        })()}
                      </div>
                    </div>
                    
                    {/* Students Table */}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee Name</TableHead>
                          <TableHead>Position</TableHead>
                          <TableHead>ID Number</TableHead>
                          <TableHead>Certificate Number</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classStudents.map((student) => {
                          const certStatus = getCertificateStatus(student.id, cls.id);
                          return (
                            <TableRow 
                              key={student.id}
                              className={!isCompanyUser ? "cursor-pointer hover:bg-slate-50" : ""}
                              onClick={() => !isCompanyUser && navigate(`/students/${student.id}`)}
                            >
                              <TableCell className={!isCompanyUser ? "font-medium text-[#2C9DE1]" : "font-medium"}>
                                {student.first_name} {student.last_name}
                              </TableCell>
                              <TableCell>{student.position || '-'}</TableCell>
                              <TableCell className="font-mono text-sm">{student.id_number || '-'}</TableCell>
                              <TableCell className="font-mono text-sm">
                                {getCertificateNumber(student.id, cls.id)}
                              </TableCell>
                              <TableCell>
                                {certStatus === 'expired' ? (
                                  <Badge className="bg-red-100 text-red-800">
                                    <X className="w-3 h-3 mr-1" />
                                    Expired
                                  </Badge>
                                ) : certStatus === 'expiring' ? (
                                  <Badge className="bg-amber-100 text-amber-800">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    &lt;90 to Expire
                                  </Badge>
                                ) : certStatus === 'certified' ? (
                                  <Badge className="bg-emerald-100 text-emerald-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Certified
                                  </Badge>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                );
              })}
                </div>
              </TabsContent>
            </Tabs>
          )}
          {yearClasses.length > CLASSES_PER_PAGE && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-slate-500" data-testid="company-classes-pagination-info">
                Showing {(classPage - 1) * CLASSES_PER_PAGE + 1} - {Math.min(classPage * CLASSES_PER_PAGE, yearClasses.length)} of {yearClasses.length} classes
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClassPage(p => Math.max(1, p - 1))}
                  disabled={classPage <= 1}
                  data-testid="company-classes-prev-btn"
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {classPage} of {Math.ceil(yearClasses.length / CLASSES_PER_PAGE)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClassPage(p => Math.min(Math.ceil(yearClasses.length / CLASSES_PER_PAGE), p + 1))}
                  disabled={classPage >= Math.ceil(yearClasses.length / CLASSES_PER_PAGE)}
                  data-testid="company-classes-next-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Employees List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2C9DE1]" />
            All Employees
          </CardTitle>
          <CardDescription>
            All employees participated at a training course from {company.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {students.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p>No employees registered for this company</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>ID Number</TableHead>
                  <TableHead>Classes Attended</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((student) => {
                  const studentClassCount = allClasses.filter(c => 
                    c.student_ids?.includes(student.id)
                  ).length;
                  const studentStatus = getStudentCertificateStatus(student.id);
                  return (
                    <TableRow 
                      key={student.id}
                      className={!isCompanyUser ? "cursor-pointer hover:bg-slate-50" : ""}
                      onClick={() => !isCompanyUser && navigate(`/students/${student.id}`)}
                    >
                      <TableCell className="font-medium">
                        <span className={!isCompanyUser ? "text-[#2C9DE1]" : ""}>{student.first_name} {student.last_name}</span>
                      </TableCell>
                      <TableCell>{student.position || '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{student.id_number || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{studentClassCount}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Per-class Send Reminder Dialog */}
      <Dialog
        open={!!reminderClass}
        onOpenChange={(o) => {
          if (!o) {
            setReminderClass(null);
            setReminderStudents([]);
            setReminderSelected([]);
          }
        }}
      >
        <DialogContent className="max-w-lg" data-testid="class-reminder-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Send Expiry Reminder
            </DialogTitle>
            <DialogDescription>
              {reminderClass && (
                <>
                  Email <strong>{company?.name}</strong> about certificates expiring in
                  the next 180 days for class <strong>{reminderClass.course_name}</strong>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {reminderClass && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div><strong>To:</strong> {company?.email || <span className="text-red-600">No email set</span>}</div>
                <div><strong>Primary Contact:</strong> {company?.primary_contact || 'Not set'}</div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Students to include:
                </label>
                <div className="max-h-56 overflow-y-auto border rounded-lg divide-y">
                  {reminderStudents.map((s) => (
                    <div
                      key={s.student_id}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                      onClick={() => toggleReminderStudent(s.student_id)}
                      data-testid={`reminder-student-row-${s.student_id}`}
                    >
                      <Checkbox
                        checked={reminderSelected.includes(s.student_id)}
                        onCheckedChange={() => toggleReminderStudent(s.student_id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{s.student_name}</div>
                        <div className="text-xs text-slate-500">
                          {s.certificates.length} expiring{' '}
                          {s.certificates.length === 1 ? 'certificate' : 'certificates'}
                          {s.certificates[0]?.expiry_date && (
                            <> · expires {formatDate(s.certificates[0].expiry_date)}</>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-sm text-slate-500">
                {reminderSelected.length} of {reminderStudents.length} students selected
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReminderClass(null)}
              data-testid="class-reminder-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendClassReminder}
              disabled={
                reminderSending ||
                reminderSelected.length === 0 ||
                !company?.email
              }
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
              data-testid="class-reminder-send-btn"
            >
              {reminderSending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetail;
